import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { type FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, LoadingSpinner } from "widgets";
import { useURLSearchParam } from "widgets/hooks";

import { CLEAN_PROJECT_ACTIVITIES } from "@/redux/action-types.js";

import { prjCommentingAllowed } from "../../app-utils/capability.js";
import { type Project, type ProjectActivity, type User } from "../../graphql.js";
import {
  loadProject,
  loadProjectActivities,
  postComment,
  unwatchProjectActivity,
  watchProjectActivity,
} from "../../redux/modules/project.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { AsyncSearchWithExprParser } from "../core/forms/AsyncSearchWithExprParser.js";
import { CommentInputForm } from "./CommentInputForm.js";
import styles from "./ProjectActivityPanel.module.css";
import { ProjectActivityStream } from "./ProjectActivityStream.js";

/**
 * The panel for the project activities of one project on the right in project overview.
 *   Contains the search field, the stack of activities and the "New Comment" field,
 */

export const ProjectActivityPanel: FC<{ projectId: number }> = ({ projectId }) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const [searchValue, setSearchValue] = useState("");
  const activityId = useURLSearchParam("activityid");
  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);

  const [isLoading, setIsLoading] = useState(false);
  const project = useSelector<IAppState, Project>((state) => state.project.project);
  const projectActivities = useSelector<IAppState, ProjectActivity[]>((state) => state.project.projectActivities);

  useEffect(() => {
    async function load() {
      setIsLoading(true);
      if (project == null || project.id !== projectId) {
        await dispatch(loadProject(projectId));
      }
      await dispatch(loadProjectActivities(projectId));
      setIsLoading(false);
    }

    void load();

    return () => {
      dispatch({ type: CLEAN_PROJECT_ACTIVITIES });
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    const activitySubscriptionId = dispatch(watchProjectActivity(projectId));

    return () => dispatch(unwatchProjectActivity(activitySubscriptionId));
  }, [projectId]);

  return (
    <Card
      className={styles.panel}
      title="Activity"
      actions={[
        <AsyncSearchWithExprParser
          onSearchExprChange={async (value) => {
            setSearchValue(value);
          }}
          key={"project-activity-panel-1"}
        />,
      ]}
      noPadding
      fillHeight
      contentStyle={{ gap: 0 }}
      data-testid="activity-panel"
    >
      <span className="hotspot-beacon" style={{ flex: "0 0 0px" }} />
      {/* For scrolling to an activity the containerElement must be positioned !
          CSS style has to set position: 'relative' or 'absolute' and overflowY: 'scroll' or 'auto'*/}
      <div className={styles.body}>
        <div className={styles.listWrapper}>
          {isLoading ? (
            <LoadingSpinner inCenter={true} />
          ) : (
            <ProjectActivityStream
              activities={projectActivities}
              filterQuery={searchValue}
              scrollToActivityId={activityId}
            />
          )}
        </div>
      </div>

      {project && prjCommentingAllowed(project) && (
        <div className={styles.footer}>
          <CommentInputForm
            onPostComment={(comment, groupIds, viewIds, flagIds, projectFileIds) =>
              dispatch(postComment(project.id, comment, groupIds, viewIds, flagIds, projectFileIds))
            }
            project={project}
            projectIsLoading={isLoading}
            currentUser={currentUser}
            buttonLabel={"Post"}
            fieldLabel={"Comment"}
            fieldPlaceholder={"Type comment here"}
            maxChars={DB_FIELD_MAX_LENGTH.PROJECT.COMMENT}
          />
        </div>
      )}
    </Card>
  );
};
